import { useState } from 'react';
import Reveal from '@/components/feature/Reveal';
import { buildYearGroups } from '@/utils/monthlyGroups';
import { formatSigned, formatBalance } from '@/utils/numberFormat';
import type { DerivedPerformance } from '@/utils/performanceData';
import MonthlyBreakdownCards from './MonthlyBreakdownCards';
import RecentTrades from './RecentTrades';

interface MonthlyResultsProps {
  data: DerivedPerformance;
}

/**
 * The month-by-month results block. It owns the "which year am I looking at"
 * state so the desktop table and the mobile cards can both be filtered, while
 * the running footer totals always match whatever is on screen.
 */
export default function MonthlyResults({ data }: MonthlyResultsProps) {
  const monthlyBreakdown = data.monthlyBreakdown;
  const yearGroups = buildYearGroups(monthlyBreakdown);
  const [selectedYear, setSelectedYear] = useState<string>('all');

  const visibleGroups =
    selectedYear === 'all' ? yearGroups : yearGroups.filter((group) => group.year === selectedYear);
  const activeGroup =
    selectedYear === 'all' ? null : yearGroups.find((group) => group.year === selectedYear) ?? null;

  const totalProfit = monthlyBreakdown.reduce((sum, row) => sum + row.profit, 0);
  const positiveMonths = monthlyBreakdown.filter((row) => row.profit >= 0).length;
  const totalPct = (monthlyBreakdown.reduce((acc, r) => acc * (1 + r.pct / 100), 1) - 1) * 100;
  const closingBalance = monthlyBreakdown.length
    ? monthlyBreakdown[monthlyBreakdown.length - 1].balance
    : 0;

  const summary = activeGroup
    ? {
        profit: activeGroup.profit,
        pct: activeGroup.compoundedPct,
        positive: activeGroup.positive,
        count: activeGroup.months.length,
        closing: activeGroup.closing,
      }
    : {
        profit: totalProfit,
        pct: totalPct,
        positive: positiveMonths,
        count: monthlyBreakdown.length,
        closing: closingBalance,
      };

  const options = [
    { id: 'all', label: 'All years' },
    ...yearGroups.map((group) => ({ id: group.year, label: group.year })),
  ];

  return (
    <>
      <Reveal delay={80}>
        <div className="mt-8 flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="inline-flex flex-wrap items-center gap-0.5 sm:gap-1 rounded-full bg-background-200/70 p-1">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setSelectedYear(option.id)}
                aria-pressed={selectedYear === option.id}
                className={`cursor-pointer whitespace-nowrap rounded-full px-2.5 sm:px-3.5 py-1 sm:py-1.5 text-[11px] sm:text-xs font-semibold transition-colors ${
                  selectedYear === option.id
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <span className="text-[11px] sm:text-xs text-foreground-500">
            {summary.count} {summary.count === 1 ? 'month' : 'months'}
          </span>
        </div>
      </Reveal>

      <Reveal delay={100} className="hidden md:block">
        <div className="mt-4 rounded-lg border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3.5 font-semibold">Month</th>
                  <th className="px-4 py-3.5 font-semibold text-right">Monthly Return</th>
                  <th className="px-4 py-3.5 font-semibold text-right">Net P/L</th>
                  <th className="px-4 py-3.5 font-semibold text-right">Closing Balance</th>
                  <th className="px-4 py-3.5 font-semibold text-right">Result</th>
                </tr>
              </thead>

              {visibleGroups.map((group) => (
                <tbody key={group.year} className="divide-y divide-background-200">
                  <tr className="bg-secondary-50">
                    <td colSpan={5} className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-secondary-200/70 text-secondary-900">
                          <i className="ri-calendar-line text-[13px]" aria-hidden="true"></i>
                        </span>
                        <span className="font-heading text-sm font-bold text-foreground-950">
                          {group.year}
                        </span>
                        <span className="hidden sm:inline text-xs text-foreground-500">
                          {group.months.length} months traded · {group.positive} profitable
                        </span>
                      </div>
                    </td>
                  </tr>

                  {group.months.map((row) => {
                    const isWin = row.profit >= 0;
                    return (
                      <tr key={row.id} className="bg-background-50 hover:bg-background-100 transition-colors">
                        <td className="px-4 py-3.5 font-medium text-foreground-900 whitespace-nowrap">
                          {row.month}
                        </td>
                        <td
                          className={`px-4 py-3.5 font-num text-right whitespace-nowrap ${
                            isWin ? 'text-primary-600' : 'text-secondary-500'
                          }`}
                        >
                          {row.pct >= 0 ? '+' : ''}
                          {row.pct.toFixed(2)}%
                        </td>
                        <td
                          className={`px-4 py-3.5 font-num font-semibold text-right whitespace-nowrap ${
                            isWin ? 'text-primary-600' : 'text-secondary-500'
                          }`}
                        >
                          {formatSigned(row.profit)}
                          <span className="ml-1 text-[10px] font-normal text-foreground-400">USC</span>
                        </td>
                        <td className="px-4 py-3.5 font-num text-foreground-800 text-right whitespace-nowrap">
                          {formatBalance(row.balance)}
                        </td>
                        <td className="px-4 py-3.5 text-right whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold ${
                              isWin ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-900'
                            }`}
                          >
                            <i
                              className={isWin ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}
                              aria-hidden="true"
                            ></i>
                            {isWin ? 'Profit' : 'Loss'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}

                  {activeGroup ? null : (
                    <tr className="bg-background-100">
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span className="font-heading text-xs font-bold uppercase tracking-wide text-foreground-700">
                          {group.year} subtotal
                        </span>
                      </td>
                      <td
                        className={`px-4 py-3.5 font-num text-right font-bold whitespace-nowrap ${
                          group.compoundedPct >= 0 ? 'text-primary-700' : 'text-secondary-500'
                        }`}
                      >
                        {group.compoundedPct >= 0 ? '+' : ''}
                        {group.compoundedPct.toFixed(1)}%
                      </td>
                      <td
                        className={`px-4 py-3.5 font-num text-right font-bold whitespace-nowrap ${
                          group.profit >= 0 ? 'text-primary-700' : 'text-secondary-500'
                        }`}
                      >
                        {formatSigned(group.profit)}
                        <span className="ml-1 text-[10px] font-normal text-foreground-400">USC</span>
                      </td>
                      <td className="px-4 py-3.5 font-num text-right font-semibold text-foreground-900 whitespace-nowrap">
                        {formatBalance(group.closing)}
                      </td>
                      <td className="px-4 py-3.5 text-right whitespace-nowrap">
                        <span className="text-xs font-medium text-foreground-500">
                          {group.positive}/{group.months.length} profitable
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              ))}

              <tfoot>
                <tr className="bg-primary-950">
                  <td className="px-4 py-4 font-heading text-sm font-bold text-background-50">
                    {selectedYear === 'all' ? 'Total' : selectedYear} · {summary.count} months
                  </td>
                  <td className="px-4 py-4 font-num text-right font-bold text-background-50 whitespace-nowrap">
                    {summary.pct >= 0 ? '+' : ''}
                    {summary.pct.toFixed(2)}%
                  </td>
                  <td className="px-4 py-4 font-num text-right font-bold text-accent-300 whitespace-nowrap">
                    {formatSigned(summary.profit)}
                    <span className="ml-1 text-[10px] font-normal text-background-200/70">USC</span>
                  </td>
                  <td className="px-4 py-4 font-num text-right font-bold text-background-50 whitespace-nowrap">
                    {formatBalance(summary.closing)}
                  </td>
                  <td className="px-4 py-4 text-right text-xs text-background-200/80 whitespace-nowrap">
                    {summary.positive} profitable
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </Reveal>

      <MonthlyBreakdownCards
        yearGroups={visibleGroups}
        totalProfit={summary.profit}
        totalPct={summary.pct}
        positiveMonths={summary.positive}
        monthCount={summary.count}
        closingBalance={summary.closing}
        showSubtotals={selectedYear === 'all'}
      />

      <p className="mt-4 text-xs text-foreground-500">
        Year subtotals show each year&apos;s compounded return; the closing balance in a subtotal
        row is that year&apos;s final month. This is a USC (US Cent) account — 1,000 USC equals USD
        10.00. Net profit is shown after a single 10.00 USC withdrawal. Figures include swap;
        commission is charged separately by the broker.
      </p>

      <RecentTrades
        trades={data.trades}
        startingCapital={data.accountSnapshot.startingDeposit - data.accountSnapshot.withdrawal}
      />
    </>
  );
}